import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, catchError, filter, from, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth';
import { AUTH_RETRY_ATTEMPTED, BYPASS_AUTH } from '../services/http/http-context';

/**
 * Adds the current bearer token to outgoing requests to our own APIs.
 *
 * Two opt-outs:
 *   1. `BYPASS_AUTH` context flag — for direct calls to presigned S3 URLs.
 *   2. URL allow-list (`shouldAddAuthHeader`) — belt-and-braces for raw S3
 *      links a caller forgot to mark.
 *
 * The MFA recovery flow deliberately keeps Cognito tokens out of
 * `AuthService` (the user is mid-recovery and the app shell must stay
 * unauthenticated), so it can't use this interceptor for its `/finalize`
 * call. It calls `fetch` directly with the explicit bearer instead — see
 * `recover-mfa.ts`. Don't add a "use Amplify session as fallback" branch
 * here: that re-couples the recovery flow to Amplify's storage, which is
 * exactly the coupling we were trying to remove.
 *
 * Also handles the `TERMS_NOT_ACCEPTED` 403 by routing the user through
 * profile completion. This is the one piece of domain-level error handling
 * that legitimately belongs here, because it must reset client-side terms
 * state *before* the navigation fires (a service-layer `catchError` would
 * race with the redirect).
 *
 * 401 recovery: when a request we *did* attach a token to comes back 401,
 * the cached `idToken` signal has gone stale — typically because the tab
 * was backgrounded long enough for the access token to expire while
 * Amplify's silent-refresh timer was throttled. We refresh once via
 * `ensureValidSession(true)` (force a Cognito round-trip — the backend
 * already disagrees with the client's JWT-exp check, so we can't trust it)
 * and retry the request with the new token, marking the retry with
 * `AUTH_RETRY_ATTEMPTED` so a second 401 propagates instead of looping.
 * Concurrent 401s share one refresh round-trip — the dedup lives in
 * `AuthService.ensureValidSession`. MFA recovery is unaffected because no
 * token is ever attached during that flow.
 */
export const httpAuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(BYPASS_AUTH) || !shouldAddAuthHeader(req.url)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const router = inject(Router);

  const handleTermsRejection = (error: { status?: number; error?: unknown }) => {
    if (error.status === 403 && isTermsNotAccepted(error.error)) {
      authService.clearTermsAcceptance();
      router.navigate(['/complete-profile'], { replaceUrl: true });
      return EMPTY;
    }
    return throwError(() => error);
  };

  const handle401 =
    (originalReq: typeof req, attachedToken: string) =>
    (error: { status?: number; error?: unknown }): Observable<HttpEvent<unknown>> => {
      if (error.status !== 401) return throwError(() => error);
      if (originalReq.context.get(AUTH_RETRY_ATTEMPTED)) return throwError(() => error);

      return from(authService.ensureValidSession(true)).pipe(
        switchMap(refreshed => {
          if (!refreshed) return throwError(() => error);
          const newToken = authService.authState().idToken;
          if (!newToken || newToken === attachedToken) return throwError(() => error);
          const retried = withAuthHeaders(originalReq, newToken);
          retried.context.set(AUTH_RETRY_ATTEMPTED, true);
          return next(retried).pipe(catchError(handleTermsRejection));
        })
      );
    };

  const token = authService.authState().idToken;
  if (token) {
    return next(withAuthHeaders(req, token)).pipe(
      catchError(handleTermsRejection),
      catchError(handle401(req, token))
    );
  }

  // Token not ready yet — wait for auth to settle, then attach and continue.
  return from(authService.authState$).pipe(
    filter(isAuthenticated => isAuthenticated),
    take(1),
    switchMap(() => {
      const newToken = authService.authState().idToken;
      if (!newToken) return next(req).pipe(catchError(handleTermsRejection));
      return next(withAuthHeaders(req, newToken)).pipe(
        catchError(handleTermsRejection),
        catchError(handle401(req, newToken))
      );
    })
  );
};

function withAuthHeaders(req: Parameters<HttpInterceptorFn>[0], token: string) {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

function shouldAddAuthHeader(url: string): boolean {
  return (
    url.includes('/v1/') ||
    url.includes('crp-api') ||
    url.includes('execute-api.eu-west-1.amazonaws.com') ||
    (url.includes('amazonaws.com') && !url.includes('cognito'))
  );
}

function isTermsNotAccepted(errorBody: unknown): boolean {
  if (!errorBody) return false;
  let parsed: unknown = errorBody;
  if (typeof errorBody === 'string') {
    try {
      parsed = JSON.parse(errorBody);
    } catch {
      return false;
    }
  }
  return (
    typeof parsed === 'object' &&
    parsed !== null &&
    'error' in parsed &&
    (parsed as Record<string, unknown>)['error'] === 'TERMS_NOT_ACCEPTED'
  );
}
